import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { fetchProjects } from "../../../api/project";
import { buildProjectsIndexParams } from "../../../utils/projectsIndexParams";
import { parseEventLinks } from "../../../api/projectPlan";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { projectColorForId } from "./projectPlanUtils";

export function ProjectPlanSidebar({
  selectedProjectId,
  planStatsByProject = {},
  onSelectProject,
  className = "",
  activeProjectCount = 0,
  draftPlans = [],
  draftsLoading = false,
  onEditDraft,
  onScheduleDraft,
  onDragDraftStart,
  onDragDraftEnd,
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadProjects = useCallback(async (searchQuery) => {
    setLoading(true);
    try {
      const res = await fetchProjects(
        buildProjectsIndexParams({
          query: searchQuery || "",
          per_page: 200,
          sortBy: "displayTitle",
          sort: "ASC",
          fields: "id,displayTitle,name,project_number,status,customer",
        })
      );
      setProjects(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => loadProjects(query), query ? 280 : 0);
    return () => clearTimeout(timer);
  }, [query, loadProjects]);

  const sortedProjects = useMemo(
    () =>
      [...projects].sort((a, b) =>
        (a.displayTitle || a.name || "").localeCompare(b.displayTitle || b.name || "", "nb")
      ),
    [projects]
  );

  const renderProjectButton = (project, isActive) => {
    const stats = planStatsByProject[String(project.id)] || {};
    const label = project.displayTitle || project.name || `#${project.id}`;
    const customer =
      project.customer?.displayTitle || project.customer?.name || project.customer_name || "";
    const barColor = projectColorForId(project.id);
    const dotClass = stats.scheduledCount > 0 ? "bg-[#10B981]" : stats.draftCount > 0 ? "bg-[#F59E0B]" : "bg-[#9AA3AF]";

    return (
      <button
        key={project.id}
        type="button"
        onClick={() => onSelectProject?.(project)}
        className={`pp-proj-item ${isActive ? "active" : ""}`}
      >
        <span className="pp-proj-handle" style={{ backgroundColor: barColor }} />
        <span className="min-w-0 flex-1">
          <div className="pp-proj-name truncate text-[13.5px] font-medium text-[#0B1220]">
            {label}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-[#6B7682]">
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`} />
            {project.project_number ? (
              <span className="font-mono text-[11px] tabular-nums">{project.project_number}</span>
            ) : null}
            {customer ? (
              <>
                <span className="h-1 w-1 rounded-full bg-[#9AA3AF]" />
                <span className="truncate">{customer}</span>
              </>
            ) : null}
            {stats.isPlanned ? <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" /> : null}
          </div>
        </span>
      </button>
    );
  };

  return (
    <aside
      className={`flex w-full shrink-0 flex-col overflow-hidden border-[#E5E9F0] bg-white lg:w-[280px] lg:border-r ${className}`}
    >
      <div className="flex items-center justify-between px-4 pb-2.5 pt-4">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#6B7682]">
          {t("project.plan.sidebar.title")}
        </h2>
        {activeProjectCount > 0 ? (
          <span className="rounded-full bg-[#EEF2F7] px-2 py-0.5 text-[10px] font-semibold text-[#364152]">
            {activeProjectCount} {t("project.plan.sidebar.active")}
          </span>
        ) : null}
      </div>

      <div className="sb-search mx-3 mb-2 flex items-center gap-2 rounded-[9px] border border-transparent bg-[#EEF2F7] px-2.5 py-1.5 focus-within:border-[#D6DCE5] focus-within:bg-white">
        <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-[#9AA3AF]" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("project.plan.sidebar.search_placeholder")}
          className="min-w-0 flex-1 border-0 bg-transparent text-[13px] text-[#0B1220] outline-none placeholder:text-[#9AA3AF]"
        />
      </div>

      <div className="proj-list flex max-h-[40vh] flex-col gap-0.5 overflow-y-auto px-2 lg:max-h-none lg:flex-1">
        <button
          type="button"
          onClick={() => onSelectProject?.(null)}
          className={`pp-proj-item ${!selectedProjectId ? "active" : ""}`}
        >
          <span
            className="pp-proj-handle"
            style={{ background: "linear-gradient(180deg, #2B6CFF, #6B8DFF)" }}
          />
          <span className="min-w-0 flex-1">
            <div className="pp-proj-name text-[13.5px] font-medium text-[#0B1220]">
              {t("project.plan.sidebar.all_projects")}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-[#6B7682]">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#10B981]" />
              <span>{activeProjectCount} {t("project.plan.sidebar.active")}</span>
            </div>
          </span>
        </button>

        {loading ? (
          <ResourceLoading />
        ) : (
          sortedProjects.map((project) =>
            renderProjectButton(project, String(selectedProjectId) === String(project.id))
          )
        )}
      </div>

      <div className="my-2.5 mx-4 h-px bg-[#E5E9F0]" />

      <div className="unsched-head flex items-center justify-between px-4 pb-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#6B7682]">
          {t("project.plan.unscheduled.title")}
        </span>
        {draftPlans.length > 0 ? (
          <span className="rounded-full bg-[#EEF2F7] px-2 py-0.5 text-[10px] font-semibold text-[#364152]">
            {draftPlans.length}
          </span>
        ) : null}
      </div>

      <div className="unsched-list flex flex-1 flex-col gap-2 overflow-y-auto px-3 pb-4">
        {draftsLoading ? (
          <p className="text-sm text-[#9AA3AF]">{t("project.plan.loading")}</p>
        ) : draftPlans.length === 0 ? (
          <p className="text-sm italic text-[#9AA3AF]">{t("project.plan.no_draft_plans")}</p>
        ) : (
          draftPlans.map((plan) => {
            const { workOrderLinks, activityLinks } = parseEventLinks(plan);
            const hours = plan.planned_hours || (workOrderLinks.length + activityLinks.length) * 8 || 8;

            return (
              <button
                key={plan.id}
                type="button"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("application/x-plan-draft-id", String(plan.id));
                  e.dataTransfer.effectAllowed = "move";
                  onDragDraftStart?.(plan);
                }}
                onDragEnd={() => onDragDraftEnd?.()}
                title={t("project.plan.unscheduled.drag_hint")}
                onClick={() => onEditDraft?.(plan)}
                className="pp-draft-card"
              >
                <div className="text-[13px] font-medium text-[#0B1220]">{plan.title}</div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-[11.5px] text-[#6B7682]">
                  {plan.project?.project_number ? <span>{plan.project.project_number}</span> : null}
                  <span className="draft-hours tabular-nums">{hours}h</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onScheduleDraft?.(plan);
                    }}
                    className="rounded-full bg-[#EEF2F7] px-2 py-0.5 text-[10.5px] font-medium text-[#364152] hover:bg-[#E6EFFF] hover:text-[#1F4ED1]"
                  >
                    {t("project.plan.schedule_plan")}
                  </button>
                </div>
              </button>
            );
          })
        )}
      </div>
    </aside>
  );
}
